import { useState } from 'react';

import type { GarmentJob } from '@/api/garmentJobs';
import { useVendors, type Vendor } from '@/api/masters';
import type { GarmentJobKind } from '@/api/generated/enums';
import { DatePicker } from '@/components/DatePicker';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { SearchPicker, type PickerOption } from '@/components/SearchPicker';
import { Field, textAreaClass } from '@/components/Field';
import { Button, Notice, SectionTitle } from '@/components/PageHeading';
import { toDayjs, toIsoDate } from '@core/format/dates';
import { dueDateProblem } from '@core/jobs/close';
import { dispatchProblems, kindLabel } from '@core/jobs/queue';

/**
 * Hand a set of garments to a vendor, and issue the challan.
 *
 * <p>
 * A hand-rolled panel rather than a `FormDrawer`, for the reason `RateCardPage`'s editor gives:
 * there is no single commit for that footer's Save to own — the vendor list is a query, the date is
 * a picker, and the dispatch is one call at the end. So it carries `enterKeyScope` itself and marks
 * its own commit button `data-enter-key-commit`.
 * </p>
 */
export function DispatchDrawer({
  kind,
  jobs,
  branchId,
  saving,
  onDispatch,
  onClose,
}: {
  kind: GarmentJobKind;
  jobs: GarmentJob[];
  branchId: number | undefined;
  saving: boolean;
  onDispatch: (vendorId: number, expectedBackOn: string | null, notes: string | null) => void;
  onClose: () => void;
}) {
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [expectedBackOn, setExpectedBackOn] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  const focusRef = useAutoFocusFirstField<HTMLDivElement>();
  const labels = kindLabel(kind);

  // Only vendors who actually do this. The capability set is what TblVendor collapsed three tables
  // into, and the server refuses a launderer sent alterations — so offering one here would be
  // presenting a choice that cannot be made.
  const vendors = useVendors(branchId, {
    capability: kind === 'Cleaning' ? 'Laundry' : 'Tailor',
    search,

    // Ten, not a hundred: this is a dropdown somebody reads at a counter, and narrowing by typing
    // is faster than scrolling a list of forty launderers. The panel says how many more matched.
    pageSize: 10,
  });

  const options: PickerOption[] = (vendors.data?.items ?? []).map((vendor) => ({
    // Stringified because `PickerOption.id` is a string: one picker in the product draws on two
    // masters at once, and a numeric key could not tell vendor 1000004 from employee 1000004.
    id: String(vendor.id),
    primary: vendor.name,
    secondary: vendor.mobile,

    // What the vendor promises, which is the figure that decides who gets the work. On the row and
    // named, rather than a "· 5d" suffix on the end of a line, which reads as anything.
    // Spread rather than set to undefined: exactOptionalPropertyTypes is on, so an absent badge
    // has to be an absent key.
    ...(vendor.standardTurnaroundDays === null
      ? {}
      : { badge: `${vendor.standardTurnaroundDays}d turnaround` }),
  }));

  const [today] = useState(() => toIsoDate(new Date()) ?? '');
  const dateProblem = dueDateProblem(expectedBackOn, today);
  const problems = [...dispatchProblems(jobs), ...(dateProblem ? [dateProblem] : [])];
  const chosen = (vendors.data?.items ?? []).find((v: Vendor) => v.id === vendorId) ?? null;
  const picked = options.find((option) => option.id === String(vendorId)) ?? null;

  return (
    <div className="fixed inset-0 z-9999 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
        onClick={onClose}
      />

      <div
        ref={focusRef}
        {...enterKeyScope}
        className="relative flex h-full w-full max-w-[560px] flex-col border-l border-[var(--overlay-border)] bg-[var(--overlay-surface)] shadow-lg"
      >
        <div className="flex items-center justify-between border-b border-border-color p-4">
          <h2 className="text-base font-semibold text-gray-900">
            Send {jobs.length} garment{jobs.length === 1 ? '' : 's'} out
          </h2>
          <button
            type="button"
            aria-label="Close"
            className="cursor-pointer text-default hover:text-dark"
            onClick={onClose}
          >
            <i className="icon icon-x" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {problems.length > 0 && (
            <Notice tone="warning" title="This cannot go out yet">
              <ul className="list-none space-y-1 ps-0">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </Notice>
          )}

          <Field
            label={`Which ${labels.vendor}`}
            hint={`Only vendors set up for ${labels.verb} are listed — the challan would come back undone otherwise.`}
            required
          >
            {/*
              Searched rather than listed. A shop with forty launderers had forty options ordered by
              whatever the query returned, and the promised turnaround — the figure that decides who
              gets the work — was a suffix on the end of a line.
            */}
            <SearchPicker
              value={picked}
              options={options}
              total={vendors.data?.total ?? 0}
              loading={vendors.isLoading}
              placeholder={`Search ${labels.vendor}`}
              onSearch={setSearch}
              onSelect={(option) => setVendorId(option === null ? null : Number(option.id))}
            />
          </Field>

          {vendors.data?.items.length === 0 && (
            <Notice tone="info" title={`No ${labels.vendor} is set up yet`}>
              Add one on the Vendors screen and tick{' '}
              {kind === 'Cleaning' ? 'Cleaning' : 'Alterations'}. Until then this work has to be
              done in the shop.
            </Notice>
          )}

          <Field
            label="Due back"
            hint={
              chosen?.standardTurnaroundDays != null
                ? `Left blank, ${chosen.name}'s usual ${chosen.standardTurnaroundDays} days is used.`
                : 'Leave blank if you have not agreed one. The garment stays off the rail until it is back either way.'
            }
          >
            <DatePicker
              className="w-full"
              value={toDayjs(expectedBackOn) ?? null}
              onChange={(value) => setExpectedBackOn(toIsoDate(value) ?? null)}
            />
          </Field>

          <Field label="Anything to tell them">
            <textarea
              className={textAreaClass}
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Field>

          <div>
            <SectionTitle>On this challan</SectionTitle>
            <ul className="mt-2 list-none space-y-1 ps-0">
              {jobs.map((job) => (
                <li key={job.id} className="flex justify-between gap-3 text-[13px]">
                  <span className="text-gray-900">{job.garmentName}</span>
                  <span className="font-mono-nums text-default">{job.serialCode}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border-color p-4">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            shortcut="save"
            data-enter-key-commit
            disabled={saving || vendorId === null || problems.length > 0}
            onClick={() =>
              vendorId !== null &&
              onDispatch(vendorId, expectedBackOn, notes.trim() === '' ? null : notes.trim())
            }
          >
            Dispatch &amp; issue challan
          </Button>
        </div>
      </div>
    </div>
  );
}
